'use strict';

import {UUID} from 'angular2-uuid';
import {Locale} from './locale.enum';

/**
 * File separator.
 */
export const sep = '/';
/**
 * Empty string value.
 */
export const empty = '';

/**
 * Time to IDLE loading page.
 */
export const loadingIdleMillis = 1000;

/**
 * Default app language.
 */
export const defaultLocale = Locale.ENGLISH.name;

/**
 * Token storage key in local storage.
 */
export const tokenKey = 'token';

export const zero = 0;
export const zeroValue = '0';
export const indexNotFound = -1;
export const indexNotFoundValue = '-1';

export const time = {
  millisInSecond: 1000,
  millisInMinute: 60 * 1000,
  millisInHour: 60 * 60 * 1000,
  millisInDay: 24 * 60 * 60 * 1000,
  millisInWeek: 7 * 24 * 60 * 60 * 1000,
  millisInMonth: 30 * 7 * 24 * 60 * 60 * 1000,
  millisInYear: 365 * 30 * 7 * 24 * 60 * 60 * 1000,

  secondsInMinute: 60,
  secondsInHour: 60 * 60,
  secondsInDay: 24 * 60 * 60,
  secondsInWeek: 7 * 24 * 60 * 60,
  secondsInMonth: 30 * 7 * 24 * 60 * 60,
  secondsInYear: 365 * 30 * 7 * 24 * 60 * 60,

  minutesInHour: 60,
  minutesInDay: 24 * 60,
  minutesInWeek: 7 * 24 * 60,
  minutesInMonth: 30 * 7 * 24 * 60,
  minutesInYear: 365 * 30 * 7 * 24 * 60,

  hoursInDay: 24,
  hoursInWeek: 7 * 24,
  hoursInMonth: 30 * 7 * 24,
  hoursInYear: 365 * 30 * 7 * 24,

  daysInWeek: 7,
  daysInMonth: 30,
  daysInYear: 365,

  weeksInMonth: 4,
  weeksInYear: 12 * 4,
};

/**
 * Route paths.
 */
export const routePath = {
  root: '',
  all: '**',
  home: 'home',
  profile: 'profile',
  notFound: '404',
  about: 'about',
  accessDenied: '403',
  contact: 'contact',
  goals: 'goal',
  consent: 'consent',
  disclaimer: 'disclaimer',
  privacy: 'privacy',
  internalError: '500',
  termsAndConditions: 'terms-and-conditions',
  cookie: 'cookie',
  copyright: 'copyright',
  admin: {
    root: 'admin',
    subscribers: 'subscribers'
  },
  login: 'sign-in',
  logout: 'sign-out',
  registration: 'sign-up'
};

// https://unicode-table.com/ru/blocks/dingbats
export const specialCharacters = {
  required: '&#10033;',

  checked: '&#10003;',
  checkMark: '&#10003;',

  cross: '&#10007;',
  declined: '&#10007;'
};

/**
 * Rest full API helper.
 */
export const urlParams = {
  id: '$id',
  size: '$size',
  page: '$page',
  limit: '$size',
  offset: 'offset'
};

export const isDefined = (val) => {
  return ("undefined" != typeof (val) && null != val);
};

export const isString = (val) => {
  return typeof (val) == "string";
};
export const isArray = (val) => {
  return val instanceof Array;
};
export const isObject = (val) => {
  return val instanceof Object;
};
export const isFunction = (val) => {
  return val instanceof Function;
};

export const isEmpty = (val) => {
  if (!isDefined(val)) {
    return true;
  }
  if (isString(val)) {
    return val.trim().length === 0;
  }
  if (isArray(val)) {
    return val.length === 0;
  }
  if (isObject(val)) {
    return sizeOf(val) === 0;
  }
};

export const getValueOrEmpty = (val) => {
  return getValueOrDefault(val, empty);
};

export const getValueOrDefault = (val, valDef) => {
  return !isEmpty(val) ? val : valDef;
};

export const isTrue = (val) => {
  return (isDefined(val) && (val === true || (typeof (val) == "string" && val.toLowerCase() === "true")));
};

export const isFalse = (val) => {
  return (isDefined(val) && (val === false || (typeof (val) == "string" && val.toLowerCase() === "false")));
};

export const isBoolean = (val) => {
  return (isDefined(val) && (val === true || val === false || (typeof (val) == "string" && (val.toLowerCase() === "true" || val.toLowerCase() === "false"))));
};

export const isNumber = (val) => {
  return ("number" == typeof val)
};

export const sizeOf = (map) => {
  if (!map) {
    return 0;
  } else if (map instanceof Array) {
    return map.length;
  } else if (Object.keys) {
    return Object.keys(map).length;
  }

  var count = 0;

  for (var p in map) {
    // hasOwnProperty check is important because
    // we don't want to count properties on the prototype chain
    // such as "get", "put", "size", or others.
    if (map.hasOwnProperty(p)) {
      count++;
    }
  }

  return count;
};

/*
 * Decodes utf-8 encoded string back into multi-byte Unicode characters.
 *
 * Can be achieved JavaScript by decodeURIComponent(escape(str)),
 * but this approach may be useful in other languages.
 *
 * @param   {string} value - UTF-8 string to be decoded back to Unicode.
 * @returns {string} Decoded Unicode string.
 */
export const utf8Decode = (value) => {
  String.fromCharCode(...[54, 55, 56, 67]);
  return value
    .replace(/[\u00e0-\u00ef][\u0080-\u00bf][\u0080-\u00bf]/g, (c) => {
      let code = ((c.charCodeAt(0) & 0x0f) << 12) | ((c.charCodeAt(1) & 0x3f) << 6) | (c.charCodeAt(2) & 0x3f);
      return String.fromCharCode(code);
    })
    .replace(/[\u00c0-\u00df][\u0080-\u00bf]/g, (c) => {
      let code = (c.charCodeAt(0) & 0x1f) << 6 | c.charCodeAt(1) & 0x3f;
      return String.fromCharCode(code);
    });
};

export const decodeBlob = (value) => {
  return new TextDecoder('UTF-8').decode(value);
};

export const hasRole = (roles, role) => {
  return roles.indexOf(role) !== indexNotFound;
};

/*
 * Encodes multi-byte Unicode string into utf-8 multiple single-byte characters
 * (BMP / basic multilingual plane only).
 *
 * Chars in range U+0080 - U+07FF are encoded in 2 chars, U+0800 - U+FFFF in 3 chars.
 *
 * Can be achieved in JavaScript by unescape(encodeURIComponent(str)),
 * but this approach may be useful in other languages.
 *
 * @param   {string} value - Unicode string to be encoded as UTF-8.
 * @returns {string} UTF8-encoded string.
 */
export const utf8Encode = (value) => {
  return value
    .replace(/[\u0080-\u07ff]/g, (c) => {
        let code = c.charCodeAt(0);
        return String.fromCharCode(0xc0 | code >> 6, 0x80 | code & 0x3f);
      }
    )
    .replace(/[\u0800-\uffff]/g, (c) => {
      let code = c.charCodeAt(0);
      return String.fromCharCode(0xe0 | code >> 12, 0x80 | code >> 6 & 0x3F, 0x80 | code & 0x3f);
    });
};

export const encodeBlob = (value) => {
  return new TextEncoder().encode(value);
};

export const ascSort = (arr, key) => sort(arr, key, true);
export const descSort = (arr, key) => sort(arr, key, false);
export const sort = (arr, key, ascending: boolean) => {
  return customSort(arr, (i1, i2) => {
    let val1 = i1[key];
    let val2 = i2[key];
    if (isFunction(val1)) {
      val1 = val1();
    }
    if (isFunction(val2)) {
      val2 = val2();
    }
    if (val1 > val2) {
      return ascending ? 1 : -1;
    }
    if (val1 < val2) {
      return ascending ? -1 : 1;
    }
    return 0;
  });
};
export const customSort = (arr, func) => {
  return (arr || []).sort(func);
};

export const uuid = () => {
  return UUID.UUID();
};

export const nowMillis = () => {
  return +new Date();
};

export const now = () => {
  return new Date();
};

export const formatTimeRude = (time: number,
                               lang = {
                                 year: [empty, '%s year ago', '%s years ago', '%s years ago'],
                                 week: [empty, '%s week ago', '%s weeks ago', '%s weeks ago'],
                                 day: [empty, '%s day ago', '%s days ago', '%s days ago'],
                                 hour: [empty, '%s hour ago', '%s hours ago', '%s hours ago'],
                                 min: [empty, '%s minute ago', '%s minutes ago', '%s minutes ago'],
                                 sec: [empty, '%s second ago', '%s seconds ago', '%s seconds ago'],
                                 now: 'just now'
                               }) => {
  let result;
  let delta = nowMillis() - time;
  delta /= 1000;
  if (delta > 365 * 30 * 24 * 60 * 60) {
    result = getText(((delta / (365 * 30 * 24 * 60 * 60)) | 0), lang.year);
  } else if (delta > 7 * 24 * 60 * 60) {
    result = getText(((delta / (7 * 24 * 60 * 60)) | 0), lang.week);
  } else if (delta > 24 * 60 * 60) {
    result = getText(((delta / (24 * 60 * 60)) | 0), lang.day);
  } else if (delta > 60 * 60) {
    result = getText(((delta / (60 * 60)) | 0), lang.hour);
  } else if (delta > 60) {
    result = getText(((delta / (60)) | 0), lang.min);
  } else if (delta > 10) {
    result = getText((delta | 0), lang.sec);
  } else {
    result = lang.now;
  }
  return result;
};

export const getText = (digit, vars) => {
  if (!isDefined(vars)) {
    return digit;
  }
  let res;
  if (isArray(vars)) {
    res = vars[1];
    let rules: any = [
      [100, [11, 12, 13, 14], 3],
      [10, [1], 1],
      [10, [2, 3, 4], 2],
      ['*', 0, 3]
    ];
    if (digit == Math.floor(digit)) {
      for (let i = 0; i < rules.length; i++) {
        let rule = rules[i];
        if (rule[0] == '*') {
          res = vars[rule[2]];
          break;
        }
        let c = rule[0] ? digit % rule[0] : digit;
        if (indexOf(rule[1], c, 0) != indexNotFound) {
          res = vars[rule[2]];
          break;
        }
      }
    } else {
      res = vars[2];
    }
  } else {
    res = vars;
  }
  res = (res || '%s').replace('%s', digit);
  return res;
};

export const indexOf = (arr, value, from) => {
  for (let i = from; i < arr.length; i++) {
    if (arr[i] == value) {
      return i;
    }
  }
  return indexNotFound;
};

export const numFormat = (num: number,
                          langs = ['', 'k', 'm', 'b', 't', 'q', 'Q', 's', 'S', 'o', 'n', 'd', 'U', 'D', 'T']) => {
  let step = 1_000;
  let value = 1;
  langs.forEach(() => value *= step);
  for (let i = langs.length; i > 0; i--) {
    let delta = num / value;
    if (delta >= 1) {
      return delta.toFixed(1) + langs[i];
    }
    value /= step;
  }
  return num;
};

// leading zeros to short form eq 1.000000001 => 1.000(5)1
export const leadingZero = (num: string, wrap = '<sub>%s</sub>', float = 2) => {
  let beforePoint = '';
  let afterPoint = '';

  let str = num + '';
  let pointReached = false;
  for (let i = 0; i < str.length; i++) {
    let s = str[i];
    if (s === '.') {
      pointReached = true;
      continue;
    }
    if (pointReached) {
      afterPoint += s;
    } else {
      beforePoint += s;
    }
  }
  if (pointReached) {
    str = parseInt(afterPoint) + '';
    let zeroCount = 0;
    for (let i = 0; i < afterPoint.length; i++) {
      if (str.padStart(i + 1, '0') === afterPoint) {
        zeroCount = i;
        break;
      }
    }
    if (zeroCount > 4) {
      afterPoint = '00' + wrap.replace('%s', (zeroCount - 2) + '') + str;
    }
    return beforePoint + '.' + afterPoint;
  }
  return beforePoint + '.00';
};
